import { createContext, useContext } from "react";
import { can, type Permission, type User } from "@zenforge/shared";

/**
 * The signed-in user, so screens can hide or disable the actions their viewer
 * cannot perform.
 *
 * Without this, every page rendered every button regardless of role: the
 * server refused the request, so nothing unsafe happened, but an intern saw a
 * full set of controls that answered with an error toast when clicked. This
 * exists to make the UI agree with what the server will allow.
 *
 * It is *only* presentation. The server checks every gated action against the
 * roles table on each request; nothing here is a security boundary.
 */
export const AuthContext = createContext<User | null>(null);

export function useAuthUser(): User | null {
  return useContext(AuthContext);
}

/** Whether the signed-in user may perform an action. */
export function usePermission(permission: Permission): boolean {
  const user = useContext(AuthContext);
  if (!user) return false;
  return can(user.permissions, permission, user.role);
}
